import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
/* THE TWO SCREENS THAT NEVER GOT A DARK MODE (2026-09-01, owner report + screenshot).
 *
 * These were the ONLY two files in `client/src` still importing `colors` — the FROZEN LIGHT
 * palette — rather than `themeVar`, which is the same key set emitted as `var(--vb-color-*, <light
 * fallback>)`. Measured, not guessed: 37 components import `themeVar`; `BriefsScreen` and
 * `PropertyTimelineScreen` imported `colors`. A hex compiled into an inline style cannot respond to
 * `prefers-color-scheme` or to `[data-theme]`, so every surface here stayed light-mode while the
 * page around it went dark — which is why the table header rendered as a near-white strip on a dark
 * ground and the address links sat at the light palette's slate on it.
 *
 * The swap is safe by construction: `themeVar` is typed `Record<keyof typeof colors, string>`, so
 * every name resolves or `tsc` fails, and each var carries its own light hex as the fallback — a
 * surface that somehow renders outside `app.css` still gets a real colour rather than `inherit`.
 */
import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import { getPropertyHistory } from "./api";
import { messageFor } from "./BriefsScreen";
import { DASH, delta, kindShort, missingFigureReason, money, shortDate, statusLabel } from "./format";
import type { BriefHistory, HistoryPoint } from "./types";
import { blockStyle } from "../ui/Block";

/**
 * ONE PROPERTY, OVER TIME — `/briefs/property/:propertyKey`.
 *
 * The "go back in time and look at the progression" ask. Every brief for one address, oldest first,
 * with the movement between them.
 *
 * WHAT EVERY NUMBER ON THIS SCREEN IS
 * -----------------------------------
 * Both the figures and the deltas are **cached display copies and arithmetic over them**, computed
 * server-side over `Decimal`. They carry no provenance and nothing may cite them. This screen is
 * the right place to see that a property moved and roughly when; it is the wrong place to read a
 * number to a client. Every point links to the brief it came from, where the figure has its
 * provenance chip and its drawer, and that is the number that goes in front of anyone.
 *
 * The screen says this out loud rather than relying on the reader to infer it, because a timeline
 * is exactly the kind of chart people screenshot.
 *
 * THE RULES THE SERVER ENFORCES AND THIS SCREEN RENDERS FAITHFULLY
 * ---------------------------------------------------------------
 *   * The FIRST point has no delta — `null`, not `0`. A first brief has nothing to have moved from,
 *     and a `0` would draw a flat line through a point that does not exist.
 *   * A brief with NO figure (queued, failed) stays on the timeline — it happened — but has no
 *     delta and does not read as a fall to zero and a jump back. The next real figure's delta is
 *     measured from the last real figure, skipping the gap rather than resetting through it.
 *   * `net_change` is `null` when fewer than two briefs carry a figure, and prints as a dash with a
 *     reason rather than as "no change", which would be a claim.
 *
 * THE KEY IN THE URL is the engine's own normalised address key, passed through exactly as the list
 * handed it over. It is never derived in the browser: a second normalisation rule that disagreed
 * about a street suffix would split one property into two, and the split would look like a real gap
 * in the record rather than a bug.
 */
export function PropertyTimelineScreen() {
  const { propertyKey = "" } = useParams<{ propertyKey: string }>();
  const [history, setHistory] = useState<BriefHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const address = history?.items.find((p) => p.report.subject_address)?.report.subject_address;
  useDocumentTitle(vbTitle(address ? `${address} — history` : "Property history"));

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!propertyKey) return;
      setLoading(true);
      getPropertyHistory(propertyKey, { signal })
        .then((payload) => {
          setHistory(payload);
          setError(null);
        })
        .catch((e) => {
          if (signal?.aborted) return;
          setError(messageFor(e));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [propertyKey],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const net = delta(history?.net_change);

  return (
    <AppShell step="briefs" content="prose">
      <div style={{ padding: "8px 0 48px" }}>
        <p style={{ fontSize: 13, margin: "0 0 12px" }}>
          <Link to="/briefs" style={{ color: colors.textSecondary }}>
            ← All briefs
          </Link>
        </p>

        <header style={{ marginBottom: 8 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 4px" }}>
            {address ?? "This property"}
          </h1>
          <p style={{ fontSize: 14, color: colors.textSecondary, margin: 0 }}>
            {history
              ? `${history.count} brief${history.count === 1 ? "" : "s"} on this property, oldest first.`
              : "Loading this property's briefs…"}
          </p>
        </header>

        {/* The provenance disclaimer sits ABOVE the numbers, not in a footnote under them. Calm,
            small, sentence case — a statement of what these figures are, not an alarm. */}
        <p
          style={{
            fontSize: 13,
            color: colors.textSecondary,
            background: colors.bgApp,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: "10px 12px",
            margin: "0 0 20px",
          }}
        >
          {/* TWO STACKED BLOCKS, ONE RIGHT EDGE  (2026-09-01, W2-UIFIX, from
              `w1-harness/brief-property@1920.png`: this notice ended at x≈1113 and the metric
              strip directly below it at x≈1343 — two edges, 230px apart, on two boxes that are
              drawn the same way and read as a pair).

              The cap was on the BOX. `measure.copy` is the measure of a PARAGRAPH, and applying
              it to a bordered region makes the region's edge a typographic accident: the box is
              structure and belongs on the page's edge, the sentence inside it is prose and belongs
              at 640. Both are true at once now — the cap moved one level in, off the border and
              onto the text it was always about. */}
          <span style={{ display: "block", ...blockStyle("copy") }}>
            The values and movements below are a cached preview kept for this timeline. They are not
            sourced figures and should not be quoted. Open any brief to see its number with the source
            it came from.
          </span>
        </p>

        {error && (
          <div
            role="alert"
            style={{
              padding: "10px 14px",
              marginBottom: 16,
              border: `1px solid ${colors.border}`,
              borderLeft: `3px solid ${colors.caution}`,
              borderRadius: 8,
              background: colors.cautionBg,
              fontSize: 14,
            }}
          >
            {error}{" "}
            <button
              type="button"
              onClick={() => load()}
              data-vb-hit="44"
              style={{
                border: "none",
                background: "none",
                padding: 0,
                fontSize: 14,
                color: colors.accent,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        )}

        {loading && !history && (
          <p style={{ fontSize: 14, color: colors.textSecondary }}>Loading…</p>
        )}

        {history && history.count === 0 && !error && (
          <p style={{ fontSize: 14, color: colors.textSecondary }}>
            No briefs on this property. {DASH} it may have been removed, or the address on later
            briefs may have been written differently enough that the engine reads it as a different
            property.
          </p>
        )}

        {history && history.count > 0 && (
          <>
            <Summary history={history} net={net} />
            <ol style={{ listStyle: "none", margin: "24px 0 0", padding: 0 }}>
              {history.items.map((point, index) => (
                <TimelinePoint
                  key={point.report.id}
                  point={point}
                  first={index === 0}
                  last={index === history.items.length - 1}
                />
              ))}
            </ol>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Summary({
  history,
  net,
}: {
  history: BriefHistory;
  net: ReturnType<typeof delta>;
}) {
  const from = shortDate(history.span.first_at);
  const to = shortDate(history.span.last_at);
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        padding: "14px 16px",
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        background: colors.bgSurface,
      }}
    >
      <Stat label="Briefs">{history.count}</Stat>
      <Stat label="Span">
        {from && to ? (from === to ? from : `${from} → ${to}`) : <Dash reason="no dates recorded" />}
      </Stat>
      <Stat label="Movement, first to last">
        {net ? (
          <span style={{ color: toneFor(net.direction) }}>{net.text}</span>
        ) : (
          <Dash reason="fewer than two briefs carry a figure" />
        )}
      </Stat>
    </div>
  );
}

function TimelinePoint({
  point,
  first,
  last,
}: {
  point: HistoryPoint;
  first: boolean;
  last: boolean;
}) {
  const { report } = point;
  const value = money(report.headline_value);
  const movement = delta(point.delta_from_previous);
  const when = shortDate(report.generated_at ?? report.created_at);
  const status = statusLabel(report.status);

  return (
    <li style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
      {/* The rail. A dot per brief and a line between them — drawn with borders rather than an
          SVG so it inherits the token colours and needs no viewBox maths at any width. */}
      <div
        aria-hidden="true"
        style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 12 }}
      >
        <span
          style={{
            width: 1,
            flex: "none",
            height: 8,
            background: first ? "transparent" : colors.border,
          }}
        />
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            flex: "none",
            background: value ? colors.accent : colors.bgSurface,
            border: `1px solid ${value ? colors.accent : colors.textSecondary}`,
          }}
        />
        <span style={{ width: 1, flex: 1, background: last ? "transparent" : colors.border }} />
      </div>

      <div style={{ flex: 1, paddingBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {value ?? <Dash reason={missingFigureReason(report.status)} />}
          </span>
          {movement ? (
            <span style={{ fontSize: 13, fontWeight: 500, color: toneFor(movement.direction) }}>
              {movement.text}
            </span>
          ) : (
            value && (
              <span style={{ fontSize: 13, color: colors.textSecondary }}>
                {first ? "first brief on record" : "no earlier figure to compare"}
              </span>
            )
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: 4,
            fontSize: 13,
            color: colors.textSecondary,
          }}
        >
          <span>{when ?? "date unknown"}</span>
          <span aria-hidden="true">·</span>
          <span>{kindShort(report.kind)}</span>
          {status && status !== "Ready" && (
            <>
              <span aria-hidden="true">·</span>
              <span>{status}</span>
            </>
          )}
          {report.view_path ? (
            <>
              <span aria-hidden="true">·</span>
              <a href={report.view_path} style={{ color: colors.accent }}>
                Open this brief
              </a>
            </>
          ) : (
            report.view_note && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {DASH} {report.view_note}
                </span>
              </>
            )
          )}
        </div>
      </div>
    </li>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          color: colors.textSecondary,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 500 }}>{children}</div>
    </div>
  );
}

/** An em dash with the reason it is one, in the sentence case the rest of the product uses. */
function Dash({ reason }: { reason: string }) {
  return (
    <span style={{ color: colors.textSecondary, fontWeight: 400 }}>
      {DASH} <span style={{ fontSize: 13 }}>{reason}</span>
    </span>
  );
}

/**
 * Colour for a movement — and it is never the only carrier. Every delta prints its sign and its
 * amount as text, so the colour is reinforcement and a reader who cannot distinguish it loses
 * nothing. Neither direction is styled as good or bad: a rise is good news for a listing and bad
 * news for a protest, and this list serves both.
 */
function toneFor(direction: "up" | "down" | "flat"): string {
  if (direction === "flat") return colors.textSecondary;
  return colors.textPrimary;
}
